import React, { useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import {
    DashboardGrid,
    ChartTile,
    TileHeader,
    TileTitle,
    NumberDisplay,
    BigNumber,
    NumberSubtext,
    NumberLoadingWrapper,
    EmptyStateMessage,
} from './ApplicationDashboard.styles';
import { BaseDashboardProps } from './dashboardTypes';
import OrgAnalyticsContainer from '../../../../../shared/hooks/OrgAnalyticsContainer';
import ListStat from '../../../../../shared/models/customAssistant/ListStat';
import SecondaryLoader from '../../../../../shared/components/general/SecondaryLoader';
import { color_shades_dark, color_shades_darker, color_shades_darkest } from '../../../../../shared/constants/colors';
import { breakpoint_macbook_small, breakpoint_macbook, breakpoint_macbook_large, breakpoint_small } from '../../../../../shared/constants/breakpoints';

interface AIAnsweringDashboardProps extends BaseDashboardProps { }

const AIAnsweringDashboard: React.FC<AIAnsweringDashboardProps> = ({
    organizationId,
    startDate,
    endDate,
    appNameMap,
    isChartsLoading,
    onLoadingChange,
}) => {
    const orgAnalyticsContainer = OrgAnalyticsContainer.useContainer();

    useEffect(() => {
        if (!organizationId) return;
        orgAnalyticsContainer.resetStats();
        orgAnalyticsContainer.loadOrgAnalytics(organizationId, { startDate, endDate });
    }, [organizationId, startDate, endDate]);

    const isCallsLoading = isChartsLoading || orgAnalyticsContainer.loadingSessionsOfAllApplications || orgAnalyticsContainer.loadingCallStatistics;
    const isTimeSavedLoading = isChartsLoading || orgAnalyticsContainer.loadingCallStatistics || !orgAnalyticsContainer.callStatistics;
    const isTopicsLoading = isChartsLoading || orgAnalyticsContainer.loadingTopIntents || !orgAnalyticsContainer.topIntents;

    const mostActiveLocations = useMemo<ListStat[]>(() => {
        const breakdown = orgAnalyticsContainer.callStatistics?.applicationBreakdown;
        if (!breakdown) return [];
        return Object.keys(breakdown)
            .sort((a, b) => breakdown[b].calls - breakdown[a].calls)
            .map(appId => ({
                name: appNameMap?.[appId] ?? appId,
                count: breakdown[appId].calls.toLocaleString(),
            }));
    }, [orgAnalyticsContainer.callStatistics, appNameMap]);

    const isLocationsLoading = isChartsLoading || orgAnalyticsContainer.loadingCallStatistics || orgAnalyticsContainer.callStatistics?.applicationBreakdown == null;

    const isAnyLoading = isCallsLoading || isTimeSavedLoading || isTopicsLoading || isLocationsLoading;
    useEffect(() => {
        onLoadingChange?.(isAnyLoading);
    }, [isAnyLoading, onLoadingChange]);

    const formatTimeSaved = (minutes: number): { value: string; unit: string } => {
        if (minutes == null) return { value: '—', unit: '' };
        if (minutes >= 120) {
            return { value: (minutes / 60).toFixed(), unit: 'Hours' };
        }
        return { value: minutes.toFixed(), unit: 'Minutes' };
    };

    const timeSaved = formatTimeSaved(orgAnalyticsContainer.callStatistics?.minutes);
    const dateRangeLabel = `${moment(startDate).format('MMM D')} – ${moment(endDate).format('MMM D')}`;

    return (
        <AIAnsweringGrid>
            {/* Calls */}
            <ChartTile>
                <TileHeader>
                    <TileTitle>Calls</TileTitle>
                </TileHeader>
                {isCallsLoading ? (
                    <NumberLoadingWrapper>
                        <SecondaryLoader className="grey" />
                    </NumberLoadingWrapper>
                ) : (
                    <NumberDisplay>
                        <BigNumber>{orgAnalyticsContainer.sessionIdCount?.toLocaleString() ?? '0'}</BigNumber>
                        <NumberSubtext>{dateRangeLabel}</NumberSubtext>
                    </NumberDisplay>
                )}
            </ChartTile>

            {/* Time Saved */}
            <ChartTile>
                <TileHeader>
                    <TileTitle>Time Saved</TileTitle>
                </TileHeader>
                {isTimeSavedLoading ? (
                    <NumberLoadingWrapper>
                        <SecondaryLoader className="grey" />
                    </NumberLoadingWrapper>
                ) : (
                    <NumberDisplay>
                        <BigNumber>
                            {timeSaved.value}
                            <UnitSpan> {timeSaved.unit}</UnitSpan>
                        </BigNumber>
                        <NumberSubtext>{dateRangeLabel}</NumberSubtext>
                    </NumberDisplay>
                )}
            </ChartTile>

            {/* Top Topics */}
            <ChartTile>
                <TileHeader>
                    <TileTitle>Top Topics</TileTitle>
                </TileHeader>
                {isTopicsLoading ? (
                    <NumberLoadingWrapper>
                        <SecondaryLoader className="grey" />
                    </NumberLoadingWrapper>
                ) : !orgAnalyticsContainer.topIntents?.length ? (
                    <EmptyStateMessage>
                        There are no topic inquiries for the selected date range. If you see calls, but no data here, it's likely due to disabled detailed analytics or hangups.
                    </EmptyStateMessage>
                ) : (
                    <StatList>
                        {orgAnalyticsContainer.topIntents.map((stat, i) => (
                            <StatListItem key={i}>
                                <StatRank>{i + 1}</StatRank>
                                <StatName>{stat.name}</StatName>
                                <StatCount>{stat.count} {Number(stat.count) === 1 ? 'Inquiry' : 'Inquiries'}</StatCount>
                            </StatListItem>
                        ))}
                    </StatList>
                )}
            </ChartTile>

            {/* Most Active Locations */}
            <ChartTile>
                <TileHeader>
                    <TileTitle>Most Active Locations</TileTitle>
                </TileHeader>
                {isLocationsLoading ? (
                    <NumberLoadingWrapper>
                        <SecondaryLoader className="grey" />
                    </NumberLoadingWrapper>
                ) : !mostActiveLocations?.length ? (
                    <EmptyStateMessage>
                        There haven't been any calls for the selected date range.
                    </EmptyStateMessage>
                ) : (
                    <StatList>
                        {mostActiveLocations.map((stat, i) => (
                            <StatListItem key={i}>
                                <StatRank>{i + 1}</StatRank>
                                <StatName>{stat.name}</StatName>
                                <StatCount>{stat.count} {Number(stat.count) === 1 ? 'Call' : 'Calls'}</StatCount>
                            </StatListItem>
                        ))}
                    </StatList>
                )}
            </ChartTile>
        </AIAnsweringGrid>
    );
};

const AIAnsweringGrid = styled(DashboardGrid)`
    grid-template-rows: minmax(240px, auto) 1fr;
    height: 100%;
    
    ${breakpoint_macbook_small} {
        grid-template-rows: minmax(240px, auto) 1fr;
    }
    
    ${breakpoint_macbook} {
        grid-template-rows: minmax(240px, auto) 1fr;
    }
    
    ${breakpoint_macbook_large} {
        grid-template-rows: minmax(240px, auto) 1fr;
    }
    
    ${breakpoint_small} {
        grid-template-rows: auto;
        height: auto;
    }
`;

const UnitSpan = styled.span`
    font-size: 0.5em;
    color: ${color_shades_dark};
    font-weight: 400;
    letter-spacing: 0;
`;

const StatList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 4px 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 8px;
    overflow-y: auto;
    min-height: 0;
    
    ${breakpoint_macbook_small} {
        gap: 6px;
    }
    
    ${breakpoint_small} {
        gap: 6px;
    }
`;

const StatListItem = styled.li`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.04);
    transition: background 0.15s ease;
    
    &:hover {
        background: rgba(0, 0, 0, 0.06);
    }
    
    ${breakpoint_small} {
        padding: 8px 10px;
    }
`;

const StatRank = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${color_shades_darker};
    min-width: 18px;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.07);
    flex-shrink: 0;
    
    ${breakpoint_small} {
        font-size: 11px;
    }
`;

const StatName = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${color_shades_darkest};
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    
    ${breakpoint_small} {
        font-size: 12px;
    }
`;

const StatCount = styled.span`
    font-size: 12px;
    color: ${color_shades_darker};
    font-weight: 600;
    white-space: nowrap;
    background: rgba(0, 0, 0, 0.05);
    padding: 2px 8px;
    border-radius: 12px;
    
    ${breakpoint_small} {
        font-size: 10px;
        padding: 1px 6px;
    }
`;

export default AIAnsweringDashboard;
export { AIAnsweringDashboard };
export type { AIAnsweringDashboardProps };
